import React, { useState, useEffect } from 'react';
import {
  Box, Flex, Heading, Text, Button, Input, Textarea, Select,
  FormControl, FormLabel, FormHelperText, SimpleGrid,
  Card, CardBody, VStack, HStack, useColorModeValue, useToast,
  Tag, Wrap, WrapItem, IconButton, Skeleton, SkeletonText,
  Alert, AlertIcon
} from '@chakra-ui/react';
import { ArrowLeft, X, Plus, Save, Edit } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Dashboard/Sidebar';
import { getModel, updateModel } from '../api/modelService';


const ModelEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [form, setForm] = useState({
    title: '', description: '', category: '', framework: '', input_type: '',
    output_type: '', accuracy: '', layers_info: '', model_size_mb: '',
    training_time_sec: '', num_classes: '', num_samples: '',
    demo_instructions: '', sample_input_description: '', metrics: '',
  });
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [modelFile, setModelFile] = useState(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const categories = ['Classification', 'NLP', 'Time-Series', 'Audio', 'Recommender', 'Computer Vision', 'Regression'];
  const frameworks = ['scikit-learn', 'PyTorch', 'TensorFlow', 'AutoGluon', 'Keras', 'ONNX', 'statsmodels', 'Other'];
  const inputTypes = ['image', 'audio', 'text', 'tabular'];

  useEffect(() => {
    const fetchModel = async () => {
      setLoading(true);
      try {
        const data = await getModel(id);
        setForm({
          title: data.title || '',
          description: data.description || '',
          category: data.category || '',
          framework: data.framework || '',
          input_type: data.input_type || '',
          output_type: data.output_type || '',
          accuracy: data.accuracy != null ? (data.accuracy * 100).toFixed(1) : '',
          layers_info: data.layers_info || '',
          model_size_mb: data.model_size_mb != null ? String(data.model_size_mb) : '',
          training_time_sec: data.training_time_sec != null ? String(data.training_time_sec) : '',
          num_classes: data.num_classes != null ? String(data.num_classes) : '',
          num_samples: data.num_samples != null ? String(data.num_samples) : '',
          demo_instructions: data.demo_instructions || '',
          sample_input_description: data.sample_input_description || '',
          metrics: data.metrics ? JSON.stringify(data.metrics, null, 2) : '',
        });
        setTags(data.tags || []);
      } catch (err) {
        toast({ title: 'Model not found', status: 'error' });
        navigate('/models');
      }
      setLoading(false);
    };
    fetchModel();
  }, [id, navigate, toast]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleSubmit = async () => {
    if (!form.title || !form.category || !form.framework || !form.input_type) {
      toast({ title: 'Missing required fields', status: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('description', form.description);
      formData.append('category', form.category);
      formData.append('framework', form.framework);
      formData.append('input_type', form.input_type);
      formData.append('output_type', form.output_type);
      
      // The server keeps omitted fields, so emptied optional fields are listed in clear_fields
      const optionalFields = {
        accuracy: form.accuracy ? parseFloat(form.accuracy) / 100 : '',
        layers_info: form.layers_info,
        model_size_mb: form.model_size_mb,
        training_time_sec: form.training_time_sec,
        num_classes: form.num_classes,
        num_samples: form.num_samples,
        demo_instructions: form.demo_instructions,
        sample_input_description: form.sample_input_description,
        metrics: form.metrics,
      };
      const cleared = [];
      Object.entries(optionalFields).forEach(([field, value]) => {
        if (value === '' || value == null) cleared.push(field);
        else formData.append(field, value);
      });
      if (cleared.length > 0) formData.append('clear_fields', cleared.join(','));
      formData.append('tags', tags.join(','));
      if (thumbnailFile) formData.append('thumbnail', thumbnailFile);
      if (modelFile) formData.append('model_file', modelFile);

      const token = localStorage.getItem('token');
      await updateModel(id, formData, token);

      toast({ title: 'Model updated successfully!', status: 'success', duration: 3000 });
      navigate(`/models/${id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast({ title: 'Update failed', description: msg, status: 'error' });
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Flex direction="row" minH="100vh" bg={pageBg}>
        <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <Box pl={sidebarCollapsed ? "60px" : "250px"} w="full" p={8}>
          <Skeleton h="40px" mb={4} w="200px" />
          <Skeleton h="300px" mb={4} />
          <SkeletonText noOfLines={6} spacing={4} />
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction="row" minH="100vh" bg={pageBg}>
      <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <Box pl={sidebarCollapsed ? "60px" : "250px"} w="full" overflowY="auto" pb={8}
        transition="padding-left 0.2s ease">
        
        {/* Header */}
        <Box px={8} py={6} borderBottom="1px" borderColor={borderColor} bg={cardBg}>
          <HStack mb={4}>
            <Button variant="ghost" leftIcon={<ArrowLeft size={18} />}
              onClick={() => navigate(`/models/${id}`)} size="sm">
              Back to Model
            </Button>
          </HStack>
          <HStack>
            <Edit size={28} color="var(--chakra-colors-teal-500)" />
            <Heading size="lg">Edit Model</Heading>
          </HStack>
          <Text color="gray.500" mt={1}>Update model metadata and configuration</Text>
        </Box>

        <Box px={8} py={6} maxW="900px">
          <VStack spacing={6} align="stretch">
            <Alert status="info" borderRadius="lg" variant="subtle">
              <AlertIcon />
              <Text fontSize="sm">
                You can update the details, the thumbnail and the model file. A new model file goes back to
                admin review before other users can see it again.
              </Text>
            </Alert>

            {/* Basic Info */}
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <Heading size="sm" mb={4}>📋 Basic Information</Heading>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Model Title</FormLabel>
                    <Input value={form.title} onChange={e => handleChange('title', e.target.value)} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea rows={4} value={form.description}
                      onChange={e => handleChange('description', e.target.value)} />
                  </FormControl>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                    <FormControl isRequired>
                      <FormLabel>Category</FormLabel>
                      <Select value={form.category}
                        onChange={e => handleChange('category', e.target.value)}>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </Select>
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Framework</FormLabel>
                      <Select value={form.framework}
                        onChange={e => handleChange('framework', e.target.value)}>
                        {frameworks.map(f => <option key={f} value={f}>{f}</option>)}
                      </Select>
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Input Type</FormLabel>
                      <Select value={form.input_type}
                        onChange={e => handleChange('input_type', e.target.value)}>
                        {inputTypes.map(t => <option key={t} value={t}>{t}</option>)}
                      </Select>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel>Output Type</FormLabel>
                    <Input value={form.output_type} onChange={e => handleChange('output_type', e.target.value)} />
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>

            {/* Performance */}
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <Heading size="sm" mb={4}>📊 Performance Metrics</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Accuracy (%)</FormLabel>
                    <Input type="number" min="0" max="100" step="0.1"
                      value={form.accuracy} onChange={e => handleChange('accuracy', e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Training Time (seconds)</FormLabel>
                    <Input type="number" step="0.01"
                      value={form.training_time_sec} onChange={e => handleChange('training_time_sec', e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Number of Classes</FormLabel>
                    <Input type="number" value={form.num_classes}
                      onChange={e => handleChange('num_classes', e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Number of Training Samples</FormLabel>
                    <Input type="number" value={form.num_samples}
                      onChange={e => handleChange('num_samples', e.target.value)} />
                  </FormControl>
                </SimpleGrid>
                <FormControl mt={4}>
                  <FormLabel>Additional Metrics (JSON)</FormLabel>
                  <Textarea rows={3} fontFamily="mono" fontSize="sm"
                    value={form.metrics} onChange={e => handleChange('metrics', e.target.value)} />
                </FormControl>
              </CardBody>
            </Card>

            {/* Architecture */}
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <Heading size="sm" mb={4}>🏗️ Architecture</Heading>
                <FormControl>
                  <FormLabel>Model Architecture / Layers</FormLabel>
                  <Textarea rows={4} fontFamily="mono" fontSize="sm"
                    value={form.layers_info} onChange={e => handleChange('layers_info', e.target.value)} />
                </FormControl>
                <FormControl mt={4}>
                  <FormLabel>Model Size (MB)</FormLabel>
                  <Input type="number" step="0.01" value={form.model_size_mb}
                    onChange={e => handleChange('model_size_mb', e.target.value)} />
                </FormControl>
              </CardBody>
            </Card>

            {/* Demo Config */}
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <Heading size="sm" mb={4}>🎮 Demo Configuration</Heading>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Demo Instructions</FormLabel>
                    <Textarea rows={3} value={form.demo_instructions}
                      onChange={e => handleChange('demo_instructions', e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Expected Input Description</FormLabel>
                    <Input value={form.sample_input_description}
                      onChange={e => handleChange('sample_input_description', e.target.value)} />
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>

            {/* Tags */}
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <Heading size="sm" mb={4}>🏷️ Tags</Heading>
                <HStack mb={3}>
                  <Input placeholder="Add a tag" value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddTag())} />
                  <IconButton icon={<Plus size={16} />} onClick={handleAddTag} colorScheme="teal" />
                </HStack>
                <Wrap spacing={2}>
                  {tags.map((tag, i) => (
                    <WrapItem key={i}>
                      <Tag size="md" colorScheme="teal" borderRadius="full">
                        {tag}
                        <IconButton icon={<X size={12} />} size="xs" variant="ghost"
                          ml={1} onClick={() => handleRemoveTag(tag)} />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </CardBody>
            </Card>

            {/* Thumbnail */}
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <Heading size="sm" mb={4}>🖼️ Update Thumbnail</Heading>
                <FormControl>
                  <Input type="file" accept="image/*" p={1}
                    onChange={e => setThumbnailFile(e.target.files[0])} />
                  <FormHelperText>Leave empty to keep existing thumbnail</FormHelperText>
                </FormControl>
              </CardBody>
            </Card>

            {/* Model file */}
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <Heading size="sm" mb={4}>📦 Replace Model File</Heading>
                <FormControl>
                  <Input type="file" accept=".skops,.onnx,.safetensors,.pkl,.joblib,.zip" p={1}
                    onChange={e => setModelFile(e.target.files[0])} />
                  <FormHelperText>
                    Leave empty to keep the current file. Accepted: .skops, .onnx, .safetensors, .pkl,
                    .joblib, .zip (max 500 MB). The live demo runs only .skops files.
                  </FormHelperText>
                </FormControl>
              </CardBody>
            </Card>

            {/* Submit */}
            <Flex justify="flex-end" gap={3}>
              <Button variant="outline" onClick={() => navigate(`/models/${id}`)} size="lg">
                Cancel
              </Button>
              <Button leftIcon={<Save size={18} />} colorScheme="teal" size="lg"
                borderRadius="xl" onClick={handleSubmit} isLoading={submitting}
                loadingText="Saving...">
                Save Changes
              </Button>
            </Flex>
          </VStack>
        </Box>
      </Box>
    </Flex>
  );
};

export default ModelEdit;
